import React, { useState, useEffect } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle
} from './ui/alert-dialog';
import { toast } from 'sonner';
import { RefreshCw, UsersRound, ListChecks, AlertTriangle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Members → Constant Contact newsletter list.
 * Chooses the target CC list and backfills existing active/lifetime members
 * into the sync queue. Queueing never calls Constant Contact — the admin
 * presses "Sync to CC" afterwards.
 */
export const MemberNewsletterCard = ({ token, ccConnected, onQueued }) => {
  const [lists, setLists] = useState([]);
  const [selectedListId, setSelectedListId] = useState('');
  const [selectedListName, setSelectedListName] = useState('');
  const [manualListId, setManualListId] = useState('');
  const [loadingLists, setLoadingLists] = useState(false);
  const [savingList, setSavingList] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [running, setRunning] = useState(false);

  const authHeaders = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (!token) return;
    let active = true;
    fetch(`${API_URL}/api/newsletter/newsletter-list`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (!active || !data) return;
        setSelectedListId(data.list_id || '');
        setSelectedListName(data.list_name || '');
      })
      .catch((e) => console.error('Failed to load newsletter list setting', e));
    return () => { active = false; };
  }, [token]);

  const loadLists = async () => {
    setLoadingLists(true);
    try {
      const res = await fetch(`${API_URL}/api/newsletter/cc-lists`, { headers: authHeaders });
      const data = await res.json();
      if (res.ok) {
        setLists(data.lists || []);
        if (!(data.lists || []).length) toast.info('No contact lists found in Constant Contact');
      } else {
        toast.error(data.detail || 'Could not load Constant Contact lists');
      }
    } catch (e) {
      toast.error('Network error loading lists');
    } finally {
      setLoadingLists(false);
    }
  };

  const saveList = async (listId, listName) => {
    setSavingList(true);
    try {
      const res = await fetch(`${API_URL}/api/newsletter/newsletter-list`, {
        method: 'PUT',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ list_id: listId || null, list_name: listName || null }),
      });
      const data = await res.json();
      if (res.ok) {
        setSelectedListId(listId || '');
        setSelectedListName(listName || '');
        toast.success(data.message);
        setPreview(null);
      } else {
        toast.error(data.detail || 'Could not save the list');
      }
    } catch (e) {
      toast.error('Network error saving the list');
    } finally {
      setSavingList(false);
    }
  };

  const runPreview = async () => {
    setPreviewing(true);
    try {
      const res = await fetch(`${API_URL}/api/newsletter/member-backfill/preview`, { headers: authHeaders });
      const data = await res.json();
      if (res.ok) setPreview(data);
      else toast.error(data.detail || 'Could not build the preview');
    } catch (e) {
      toast.error('Network error building the preview');
    } finally {
      setPreviewing(false);
    }
  };

  const runBackfill = async () => {
    setRunning(true);
    try {
      const res = await fetch(`${API_URL}/api/newsletter/member-backfill`, {
        method: 'POST', headers: authHeaders,
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(data.message, { duration: 10000 });
        setConfirmOpen(false);
        await runPreview();
        if (onQueued) onQueued();
      } else {
        toast.error(data.detail || 'Backfill failed');
      }
    } catch (e) {
      toast.error('Network error running the backfill');
    } finally {
      setRunning(false);
    }
  };

  return (
    <Card className="mb-6" data-testid="member-newsletter-card">
      <CardContent className="p-4 space-y-4">
        <div className="flex items-start gap-3">
          <UsersRound size={18} className="text-cafa-red mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="font-medium text-stone-900">Members → Newsletter List</h3>
            <p className="text-sm text-stone-500">
              Active and lifetime members are queued automatically when they join, renew, or are
              marked active. Queueing never contacts Constant Contact — press <strong>Sync to CC</strong> to send them.
            </p>
          </div>
        </div>

        {/* List selection */}
        <div className="border border-stone-200 rounded-md p-3 bg-stone-50">
          <div className="flex items-center gap-2 mb-2">
            <ListChecks size={15} className="text-stone-500" />
            <Label className="font-medium text-stone-800">Target Constant Contact list</Label>
          </div>
          {selectedListId ? (
            <p className="text-sm text-emerald-800 mb-2" data-testid="selected-cc-list">
              Currently sending to <strong>{selectedListName || selectedListId}</strong>
            </p>
          ) : (
            <p className="text-sm text-amber-800 mb-2 flex items-start gap-2" data-testid="no-cc-list-warning">
              <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
              <span>
                No list selected. Synced contacts are created in Constant Contact but land on no
                list, so they will not receive list campaigns.
              </span>
            </p>
          )}

          <div className="flex flex-wrap items-end gap-2">
            <div className="min-w-[240px]">
              <Label className="text-xs text-stone-500">Choose from your Constant Contact lists</Label>
              <Select
                value={selectedListId || undefined}
                onValueChange={(v) => {
                  const found = lists.find(l => l.list_id === v);
                  saveList(v, found?.name);
                }}
                disabled={!lists.length}
              >
                <SelectTrigger className="mt-1" data-testid="cc-list-select">
                  <SelectValue placeholder={lists.length ? 'Select a list' : 'Load lists first'} />
                </SelectTrigger>
                <SelectContent>
                  {lists.map(l => (
                    <SelectItem key={l.list_id} value={l.list_id}>
                      {l.name}{typeof l.membership_count === 'number' ? ` (${l.membership_count})` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button
              variant="outline"
              onClick={loadLists}
              disabled={loadingLists || !ccConnected}
              title={ccConnected ? 'Read your lists from Constant Contact' : 'Connect Constant Contact first'}
              data-testid="load-cc-lists-btn"
            >
              {loadingLists
                ? <><RefreshCw size={15} className="mr-1 animate-spin" /> Loading...</>
                : <><RefreshCw size={15} className="mr-1" /> Load Lists</>}
            </Button>
          </div>

          <div className="mt-3 flex flex-wrap items-end gap-2">
            <div className="min-w-[240px]">
              <Label className="text-xs text-stone-500">Or paste a list ID manually</Label>
              <Input
                value={manualListId}
                onChange={(e) => setManualListId(e.target.value)}
                placeholder="e.g. 1a2b3c4d-5e6f-..."
                className="mt-1"
                data-testid="cc-list-manual-input"
              />
            </div>
            <Button
              variant="outline"
              onClick={() => saveList(manualListId.trim(), 'Manually entered list')}
              disabled={savingList || !manualListId.trim()}
              data-testid="cc-list-manual-save"
            >
              Save List ID
            </Button>
            {selectedListId && (
              <Button
                variant="ghost"
                className="text-stone-500"
                onClick={() => saveList('', '')}
                disabled={savingList}
                data-testid="cc-list-clear"
              >
                Clear
              </Button>
            )}
          </div>
        </div>

        {/* Backfill */}
        <div className="border border-stone-200 rounded-md p-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <p className="font-medium text-stone-800 text-sm">Backfill existing members</p>
              <p className="text-xs text-stone-500">
                Queues every active and lifetime member. Preview first — nothing is written until you confirm.
              </p>
            </div>
            <Button variant="outline" onClick={runPreview} disabled={previewing} data-testid="member-backfill-preview-btn">
              {previewing
                ? <><RefreshCw size={15} className="mr-1 animate-spin" /> Checking...</>
                : 'Preview Backfill'}
            </Button>
          </div>

          {preview && (
            <div className="mt-3 text-sm bg-stone-50 border border-stone-200 rounded p-3" data-testid="member-backfill-preview">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-2">
                <div><p className="text-xs text-stone-500">Active / lifetime members</p><p className="font-semibold text-stone-900" data-testid="backfill-total">{preview.total_members}</p></div>
                <div><p className="text-xs text-stone-500">Will be added</p><p className="font-semibold text-emerald-700" data-testid="backfill-will-add">{preview.will_add}</p></div>
                <div><p className="text-xs text-stone-500">Will be re-queued</p><p className="font-semibold text-amber-700" data-testid="backfill-will-requeue">{preview.will_requeue}</p></div>
                <div><p className="text-xs text-stone-500">Already up to date</p><p className="font-semibold text-stone-600" data-testid="backfill-unchanged">{preview.already_on_list}</p></div>
              </div>
              {preview.skipped_no_email > 0 && (
                <p className="text-xs text-stone-500">{preview.skipped_no_email} member(s) skipped — no email address on file.</p>
              )}
              {!preview.list_configured && (
                <p className="text-xs text-amber-800">Select a list above first, otherwise these contacts will not join any list.</p>
              )}
              {(preview.samples?.created || []).length > 0 && (
                <div className="mt-2">
                  <p className="text-xs text-stone-500 mb-1">Sample of members that will be added:</p>
                  <ul className="text-xs text-stone-700 list-disc list-inside">
                    {preview.samples.created.map((m, i) => (
                      <li key={i}>{m.name} — {m.email}{m.lifetime ? ' (lifetime)' : ''}</li>
                    ))}
                  </ul>
                </div>
              )}
              <Button
                className="mt-3 bg-cafa-red hover:bg-cafa-red-900"
                onClick={() => setConfirmOpen(true)}
                disabled={preview.will_add + preview.will_requeue === 0}
                data-testid="member-backfill-run-btn"
              >
                Queue {preview.will_add + preview.will_requeue} Member(s)
              </Button>
            </div>
          )}
        </div>
      </CardContent>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="member-backfill-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Queue members for Constant Contact?</AlertDialogTitle>
            <AlertDialogDescription>
              This adds {preview ? preview.will_add + preview.will_requeue : 0} member(s) to the
              newsletter sync queue in Emergent. Nothing is sent to Constant Contact yet, no member
              record is changed, and no email goes out. Press "Sync to CC" afterwards to upload them.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={runBackfill} disabled={running} className="bg-cafa-red hover:bg-cafa-red-900">
              {running ? 'Queueing...' : 'Queue Members'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};
